/**
 * SMS Settings
 * Configure Twilio A2P 10DLC, message templates, and preferences
 */

import React, { useState } from 'react';
import { AdminPageLayout } from '@/components/layout/AdminPageLayout';
import { Card } from '@/components/ui/card';

export default function SMSSettings() {
  const [settings, setSettings] = useState({
    // Twilio A2P 10DLC
    twilioAccountSid: '',
    twilioAuthToken: '',
    messagingServiceSid: '',
    brandRegistered: false,
    
    // Templates
    enableWelcomeMessage: true,
    enablePaymentReminders: true,
    enableBookingConfirmations: true,
    
    // Quiet Hours
    quietHoursStart: '20:30',
    quietHoursEnd: '08:00',
    
    // Rate Limiting
    maxDailyPerCustomer: 3,
    maxWeeklyPerCustomer: 10
  });

  const [saved, setSaved] = useState(false);

  const saveSettings = () => {
    console.log('Saving SMS settings:', settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <AdminPageLayout
      title="SMS Settings"
      subtitle="Configure Twilio A2P 10DLC and messaging preferences"
      actions={
        <button
          onClick={saveSettings}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          {saved ? '✅ Saved!' : 'Save Settings'}
        </button>
      }
    >
      <div className="max-w-4xl space-y-6">
        {/* Twilio Configuration */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Twilio A2P 10DLC Configuration</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account SID</label>
              <input
                type="password"
                value={settings.twilioAccountSid}
                onChange={(e) => setSettings({...settings, twilioAccountSid: e.target.value})}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Messaging Service SID</label>
              <input
                type="text"
                value={settings.messagingServiceSid}
                onChange={(e) => setSettings({...settings, messagingServiceSid: e.target.value})}
                placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="w-full px-3 py-2 border rounded"
              />
            </div>

            <div className={`p-4 rounded ${settings.brandRegistered ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">A2P 10DLC Brand Registration</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {settings.brandRegistered 
                      ? '✅ Brand registered and approved'
                      : '⚠️ Brand registration required for SMS delivery'}
                  </div>
                </div>
                {!settings.brandRegistered && (
                  <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                    Register Brand
                  </button>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Message Templates */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Message Templates</h2>
          <div className="space-y-3">
            <ToggleSetting
              label="Welcome Messages"
              description="Send welcome SMS to new customers"
              value={settings.enableWelcomeMessage}
              onChange={(v) => setSettings({...settings, enableWelcomeMessage: v})}
            />
            <ToggleSetting
              label="Payment Reminders"
              description="Automated payment reminders (T-7, T-3, T-1)"
              value={settings.enablePaymentReminders}
              onChange={(v) => setSettings({...settings, enablePaymentReminders: v})}
            />
            <ToggleSetting
              label="Booking Confirmations"
              description="Confirm service appointments via SMS"
              value={settings.enableBookingConfirmations}
              onChange={(v) => setSettings({...settings, enableBookingConfirmations: v})}
            />
          </div>
        </Card>

        {/* Quiet Hours */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quiet Hours (TCPA Compliance)</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quiet Hours Start</label>
              <input
                type="time"
                value={settings.quietHoursStart}
                onChange={(e) => setSettings({...settings, quietHoursStart: e.target.value})}
                className="w-full px-3 py-2 border rounded"
              />
              <p className="text-xs text-gray-500 mt-1">Default: 8:30 PM</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quiet Hours End</label>
              <input
                type="time"
                value={settings.quietHoursEnd}
                onChange={(e) => setSettings({...settings, quietHoursEnd: e.target.value})}
                className="w-full px-3 py-2 border rounded"
              />
              <p className="text-xs text-gray-500 mt-1">Default: 8:00 AM</p>
            </div>
          </div>
        </Card>

        {/* Rate Limiting */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Rate Limiting</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Daily Messages per Customer</label>
              <input
                type="number"
                value={settings.maxDailyPerCustomer}
                onChange={(e) => setSettings({...settings, maxDailyPerCustomer: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Weekly Messages per Customer</label>
              <input
                type="number"
                value={settings.maxWeeklyPerCustomer}
                onChange={(e) => setSettings({...settings, maxWeeklyPerCustomer: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
          </div>
        </Card>
      </div>
    </AdminPageLayout>
  );
}

interface ToggleSettingProps {
  label: string;
  description: string;
  value: boolean;
  onChange: (value: boolean) => void;
}

function ToggleSetting({ label, description, value, onChange }: ToggleSettingProps) {
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
      <div>
        <div className="font-medium text-gray-900">{label}</div>
        <div className="text-sm text-gray-600">{description}</div>
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          value ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            value ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}
